import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { LoginService } from "@app/login/login.service";
import { PortalUser } from "@app/model/portal-user.model";
import { AlertsService } from "@app/services/misc-services/alerts.service";
import { Router } from "@angular/router";
import { TicketsService } from "@app/services/tickets/tickets.service";
import moment from "moment";
import { TimeListEnum } from "@app/pages/deployments/time-list.enum";

@Component({
    selector: 'flex-deployments',
    templateUrl: './deployments.component.html',
    styleUrls: ['./deployments.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeploymentsComponent implements OnInit {
    activeUser: PortalUser;
    minDate;
    maxDate;
    deploymentReason: string = '';
    deploymentDate: Date;
    deploymentTime: string = '';
    deploymentFrequency: string = '';
    deploymentTicketTitle: string = '';
    deploymentTicketNotes: string = '';
    recurring: boolean = false;
    DISABLED_DATES: any = {
        STATIC_HOLIDAYS: ['01-01', '06-19', '07-04', '10-09','11-11', '12-24', '12-25', '12-31'],
        WEEKENDS: [0, 6]
    }
    today: any = this.convertDate(new Date())

    timeList: string[] = [];
    exceptionTimes: string[] = [TimeListEnum.sevenAM] // after 4pm in Utah, these times will be removed from the times list
     frequencyList: ({ label: string; value: number })[] = [
        {label:'Monthly', value: 12},
        {label:'Quarterly', value: 4},
        {label:'Semi-annual', value: 3},
    ]
    constructor(private http: HttpClient, private currentUser: LoginService, private alert: AlertsService, private router: Router, public ticketsService: TicketsService) {
    }
    ngOnInit(): void {
        this.activeUser = this.currentUser.activeUser;
        this.minDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
        this.maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1))
        this.timeList = Object.values(TimeListEnum);
    }

    calendarFilter = (d: Date): boolean => {
        let monday: any = null;
        let dayString: string = null;
        let day = parseInt(this.today.MONTH_DAY.substr(-2))
        let month = this.today.MONTH_DAY.substr(0, 2)

        const DAY = this.convertDate(d)
        //Switch case default returns standard date exclusions including 2021 conference dates
        //Cases 0 and 6 exclude the following monday depending on the current day of the week (6 being saturday, 0 being sunday)
        //It then gets the current date of the month, adds either 1 or 2 and converts that back to a string to compare against the
        //values returned from the convertDate method
        switch (new Date().getDay()) {
            case 0:
                dayString = '0' + (day + 1).toString()
                monday = month + '-' + dayString
                return  !this.DISABLED_DATES.STATIC_HOLIDAYS?.includes(DAY.MONTH_DAY) &&
                        !this.DISABLED_DATES.WEEKENDS?.includes(DAY.DAY_OF_WEEK) &&
                        DAY.MONTH_DAY !== monday
            case 6:
                dayString = '0' + (day + 2).toString()
                monday = month + '-' + dayString
                return  !this.DISABLED_DATES.STATIC_HOLIDAYS?.includes(DAY.MONTH_DAY) &&
                        !this.DISABLED_DATES.WEEKENDS?.includes(DAY.DAY_OF_WEEK) &&
                        DAY.MONTH_DAY !== monday
            default:
                return  !this.DISABLED_DATES.STATIC_HOLIDAYS?.includes(DAY.MONTH_DAY) &&
                        !this.DISABLED_DATES.WEEKENDS?.includes(DAY.DAY_OF_WEEK)
        }
    }
    //Converts date to object that contains YYYY-MM-DD and MM-DD, and day of the week as a number
    convertDate(d: Date): any {
        let date: any;
        let month: any;

        if(moment.isMoment(d)){
            d = new Date(d)
        }

        if (d.getDate().toString().length < 2) {
            date = '0' + d.getDate().toString()
        } else {
            date = d.getDate().toString()
        }

        if ((d.getMonth() + 1).toString().length < 2) {
            month = '0' + (d.getMonth() + 1).toString()
        } else {
            month = (d.getMonth() + 1).toString()
        }
        return {
            FULL_DATE: d.getFullYear().toString() + "-" + month + "-" + date,
            MONTH_DAY: month + "-" + date,
            DAY_OF_WEEK: d.getDay()
        };
    }
    toggleRecurring() {
        this.recurring = !this.recurring;
    }
    setDeploymentRequest() {
        if (!this.deploymentDate || !this.deploymentTime || !this.deploymentReason) {
            this.alert.getError("Please complete all fields before adding a deployment, if you're requesting recurring deployments, you must select a frequency.", 'X')
        } else {
            this.deploymentTicketTitle = 'Deployment requested by ' + this.activeUser.firstName + ' ' + this.activeUser.lastName +
                ' for ' + this.activeUser.cuid + ' on ' + moment(this.deploymentDate).format('M/D/YYYY') + ' at ' + this.deploymentTime;
            if (this.deploymentFrequency && this.recurring === true) {
                this.deploymentTicketNotes = `Recurring ${this.deploymentFrequency} deployments\nReason for deployment: \n${this.deploymentReason}`;
            } else {
                this.deploymentTicketNotes = `Single deployment\nReason for deployment: \n${this.deploymentReason}`
            }
            this.createDeployment();
        }
    }

    createDeployment(){
        const ticket = this.ticketsService.createTicketDTO(this.activeUser, this.deploymentTicketTitle)
        const note = this.ticketsService.createNoteDTO(this.deploymentTicketNotes, this.activeUser, 0)
        this.ticketsService.createDeployment(ticket, note, this.activeUser).subscribe(() => {
            this.alert.getAlert('Deployment requested!', 'X');
            this.router.navigate(['/news'])
        },
            () => {
            this.alert.getError('Unable to request deployment, try again.', 'X')
            })
    }

    clearDeploymentForm(){
        this.deploymentDate = null;
        this.deploymentTime = '';
        this.deploymentReason = '';
        this.deploymentFrequency = '';
    }
    returnToNews() {
        this.router.navigate(['/news'])
    }
}
